/**
 * <AddSchoolAssessmentModal> — add-school-assessment flow shell (template, L0.1).
 *
 * Owns `ai` | `manual` mode, Modal chrome, and the "C'est parti !" footer. Draft state is a single
 * `createAssessmentData` blob; catalogs + media + analyze/submit effects are injected by the Screen.
 *
 * Le pied sait aussi dire pourquoi il attend : une erreur (`errorMessage`, celle de l'analyse ou de
 * l'enregistrement) et, sur demande (`showSubmitHint`), ce qui manque au formulaire pour valider.
 * Rien de tout cela par défaut : la feuille d'avant.
 */
import React, { useEffect, useState } from "react"

import {
    applySchoolAssessmentPreview,
    canSubmitCreateSchoolAssessment,
} from "../../../ports/primary/ui/formatters/createSchoolAssessmentFormatter"
import { type UICreateSchoolAssessment, type UISectionedChapter, type UISubject } from "../../../ports/primary/ui/types"
import { Banner } from "../../atoms/Banner"
import { Button } from "../../atoms/Buttons/Button"
import { IconButton } from "../../atoms/Buttons/IconButton"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { type IconName } from "../../atoms/icons/Icon"
import { Text } from "../../atoms/typography/Text"
import { Modal } from "../../molecules/Modal"
import { AddSchoolAssessmentMedia, type SchoolAssessmentAnalyzeStage } from "../../organisms/AddSchoolAssessmentMedia"
import { type MediaPickerProps } from "../../organisms/MediaPicker"
import { SchoolAssessmentForm } from "../../organisms/SchoolAssessmentForm"
import { type Voice } from "../../utils/voice"

/** L'étape de saisie : la photo que l'IA lit, ou le formulaire rempli à la main. */
export type AddSchoolAssessmentMode = "ai" | "manual"

/** Ce qui manque au formulaire pour valider, dit à qui le remplit. */
const TEXTS: Record<Voice, { subjectHint: string; chaptersHint: string }> = {
    student: {
        subjectHint: "Choisis une matière pour continuer",
        chaptersHint: "Choisis au moins un chapitre pour continuer",
    },
    parent: {
        subjectHint: "Choisissez une matière pour continuer",
        chaptersHint: "Choisissez au moins un chapitre pour continuer",
    },
}

export type AddSchoolAssessmentModalProps = {
    visible: boolean
    onClose: () => void
    createAssessmentData: UICreateSchoolAssessment
    onCreateAssessmentDataChange: (next: UICreateSchoolAssessment) => void
    subjects: UISubject[]
    chapters?: UISectionedChapter[]
    chaptersLoading?: boolean
    mediaProps?: MediaPickerProps
    /**
     * Hide without closing (camera / Genius Scan). Screen sets this from
     * `state.activity.cameraOn`.
     */
    hidden?: boolean
    onAnalyze?: () => void
    analyzeLoading?: boolean
    /** L'étape de l'analyse en cours, nommée sous le bouton « Analyser » — rien par défaut. */
    analyzeStage?: SchoolAssessmentAnalyzeStage | null
    onSubmit?: () => void
    /** Override derived can-submit; omit to use subject + chapters on the draft. */
    submitDisabled?: boolean
    submitLoading?: boolean
    /** Le libellé du bouton final — « C'est parti ! » par défaut ; « Enregistrer » en modification. */
    submitLabel?: string
    /** Le glyphe du bouton final — `check` par défaut ; `null` le retire. */
    submitIconName?: IconName | null
    /**
     * L'erreur de l'analyse ou de l'enregistrement, en bandeau au-dessus du bouton : la feuille
     * reste ouverte, le brouillon et les médias restent là. Rien par défaut.
     */
    errorMessage?: string | null
    /**
     * Écrit au-dessus du bouton grisé ce qui manque au formulaire (une matière, puis un chapitre).
     * Défaut `false`.
     */
    showSubmitHint?: boolean
    /** Edit flow starts on the manual form (legacy parity). */
    isEditing?: boolean
    /** L'étape à l'ouverture — la photo en ajout, le formulaire en modification, par défaut. */
    initialMode?: AddSchoolAssessmentMode
    /** Who reads the AI-step intro — forwarded to `<AddSchoolAssessmentMedia>`. */
    voice?: Voice
    title?: string
    testID?: string
}

export function AddSchoolAssessmentModal(props: AddSchoolAssessmentModalProps) {
    const {
        visible,
        onClose,
        createAssessmentData,
        onCreateAssessmentDataChange,
        subjects,
        chapters,
        chaptersLoading = false,
        mediaProps,
        hidden = false,
        onAnalyze,
        analyzeLoading = false,
        analyzeStage = null,
        onSubmit,
        submitDisabled,
        submitLoading = false,
        submitLabel = "C'est parti !",
        submitIconName = "check",
        errorMessage,
        showSubmitHint = false,
        isEditing = false,
        initialMode = isEditing ? "manual" : "ai",
        voice = "student",
        title = "Ajouter un contrôle",
        testID,
    } = props

    const [mode, setMode] = useState<AddSchoolAssessmentMode>(initialMode)

    // La feuille repart de son étape d'ouverture à chaque fois qu'elle s'ouvre ou se referme.
    useEffect(() => {
        setMode(initialMode)
    }, [visible, initialMode])

    const disabledSubmit = submitDisabled ?? !canSubmitCreateSchoolAssessment(createAssessmentData)

    const onEditPreview = () => {
        const preview = createAssessmentData.aiPreview
        if (preview == null) return
        onCreateAssessmentDataChange(applySchoolAssessmentPreview(createAssessmentData, preview))
        setMode("manual")
    }

    // Edit has no AI step — back would leave an empty media screen.
    const headerLeft =
        mode === "manual" && !isEditing ? (
            <IconButton
                iconName="chevron-left"
                appearance="transparent"
                iconColor="primary"
                onPress={() => setMode("ai")}
                accessibilityLabel="Retour"
            />
        ) : undefined

    const hasError = errorMessage != null && errorMessage !== ""
    // L'aide se tait devant une erreur : une seule phrase à la fois au-dessus du bouton.
    const hint = showSubmitHint && mode === "manual" && !hasError ? submitHint(createAssessmentData, voice) : null

    const submitButton = (
        <Row justify="center">
            <Button
                label={submitLabel}
                iconName={submitIconName ?? undefined}
                disabled={disabledSubmit}
                loading={submitLoading}
                onPress={onSubmit}
            />
        </Row>
    )

    return (
        <Modal
            visible={visible}
            onClose={onClose}
            title={title}
            testID={testID}
            fullScreenOnSmall
            hidden={hidden}
            headerLeft={headerLeft}
            footer={
                hasError || hint != null ? (
                    <Stack gap="3">
                        {hasError && <Banner message={errorMessage} color="danger" icon="exclamation-circle" tinted />}
                        {hint != null && (
                            <Text size="text-sm" color="text-muted" center>
                                {hint}
                            </Text>
                        )}
                        {submitButton}
                    </Stack>
                ) : (
                    submitButton
                )
            }
        >
            {mode === "ai" ? (
                <AddSchoolAssessmentMedia
                    mediaProps={mediaProps}
                    onAnalyze={onAnalyze}
                    analyzeLoading={analyzeLoading}
                    analyzeStage={analyzeStage}
                    preview={createAssessmentData.aiPreview}
                    onEditPreview={onEditPreview}
                    onManualPress={() => setMode("manual")}
                    voice={voice}
                />
            ) : (
                <SchoolAssessmentForm
                    value={createAssessmentData}
                    onChange={onCreateAssessmentDataChange}
                    subjects={subjects}
                    chapters={chapters}
                    chaptersLoading={chaptersLoading}
                    disabled={submitLoading}
                />
            )}
        </Modal>
    )
}

/** La première chose qui manque au brouillon — rien quand il peut partir. */
function submitHint(draft: UICreateSchoolAssessment, voice: Voice): string | null {
    if (draft.subjectId == null) return TEXTS[voice].subjectHint
    if (draft.chapterIds.length === 0 && draft.customChapters.length === 0) return TEXTS[voice].chaptersHint
    return null
}
